/**
 * The reminder bar for an employee whose verification is not done (6 Oct
 * 2026): not started, not sent, or sent back. Gone once it is sent or
 * verified, and never shown to a mitra, outlet or dealer — or to a head-admin
 * desk, which is the owner's door and not an employee (12 Oct 2026).
 */
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { isVerifiedEmployee } from '@bright/shared';

import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Verification } from './VerificationForm';

export function VerificationNudge() {
  const { user } = useAuth();
  const location = useLocation();
  const employee = Boolean(user) && isVerifiedEmployee({ roleKey: user!.role.key, email: user!.email, seatOfId: user!.seatOf?.id });
  const query = useQuery({
    queryKey: ['verification', '/verification/mine'],
    queryFn: () => api.get<Verification>('/verification/mine', undefined, { book: null }),
    enabled: employee,
    staleTime: 5 * 60_000,
  });
  if (!employee || location.pathname === '/my-verification' || !query.data) return null;
  const status = query.data.status;
  if (status === 'SUBMITTED' || status === 'VERIFIED') return null;
  return (
    <Link
      to="/my-verification"
      className="mb-3 flex items-center gap-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-caption text-ink-2 hover:border-accent"
    >
      <ShieldCheck className="h-4 w-4 shrink-0 text-accent" aria-hidden />
      <span className="min-w-0 flex-1">
        {status === 'REJECTED'
          ? 'The office sent your verification back — see what to fix.'
          : 'Verify your Aadhaar and details — it takes two minutes.'}
      </span>
      <span className="shrink-0 font-medium text-accent">Open</span>
    </Link>
  );
}
